import React, { FC, useState, useEffect } from 'react'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import { Image, Spinner } from 'ui'
import { Table, TableBody, TableCell, TableRow } from '@material-ui/core'
import { History, ArrowBack } from '@material-ui/icons'
import ProtocolsEmpty from './ProtocolsEmpty'
import MekomeTablePagination from 'components/MekomeTablePagination'
import useStore from 'store'
import { STATUS_MAP } from './statusMap'
import DateInputWithCalendar from '../PopulationManagement/CalendarInput'

import {
  TableHeadStyled,
  TableContainerStyled,
  TableCellStyled,
  TableCellCentered,
  HeadCell,
  HeadCellCentered,
} from './ArchiveList.styles'

import { StyledLabel } from './Protocols.styles'

import {
  HistoryHeader,
  HistoryHeaderTopRow,
  HeaderTitleGroup,
  BackButton,
  HistoryTitle,
  ClockIconButton,
  Breadcrumb,
  BreadcrumbActive,
  FilterCardRow,
  ProtocolBadgeCard,
  ProtocolBadgeTexts,
  ProtocolBadgeLabel,
  ProtocolBadgeTitle,
  DateFieldsRow,
  DateField,
  HistoryBody,
  StatusBadge,
  StatusTransitionCell,
  EditorCell,
  DateCell,
  NoteCell,
} from './ProtocolStatusHistoryView.styles'

import DocIcon from 'assets/icons/protocols/icon12.svg'
import User4Icon from 'assets/icons/ims/User4.svg'

type Props = {
  protocolId: number
  protocolTitle: string
  protocolDate?: string
  onBack: () => void
}

const ProtocolStatusHistoryView: FC<Props> = ({ protocolId, protocolTitle, protocolDate, onBack }) => {
    const { t } = useTranslation('protocols')
  const { protocolsStore, user } = useStore()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  // Default the range to [protocol's date .. today] so the log opens showing the
  // full lifespan of the protocol rather than an unbounded/empty filter.
  const [fromDate, setFromDate] = useState<Date | null>(() => (protocolDate ? new Date(protocolDate) : null))
  const [toDate, setToDate] = useState<Date | null>(() => new Date())

  useEffect(() => {
    const id = setTimeout(() => {
      protocolsStore.loadProtocolStatusHistory(
        protocolId,
        page + 1,
        rowsPerPage,
        fromDate ? fromDate.toISOString() : undefined,
        toDate ? toDate.toISOString() : undefined,
      )
    }, 350)
    return () => clearTimeout(id)
  }, [protocolId, page, rowsPerPage, fromDate, toDate])

  const { protocolStatusHistory, protocolStatusHistoryTotalCount, loadingProtocolStatusHistory } = protocolsStore

  const renderStatus = (statusId: number) => {
    const status = STATUS_MAP[statusId] || { titleKey: String(statusId), color: '#4C5564', bg: '#F1F5F9' }
    return <StatusBadge $color={status.color} $bg={status.bg}>{t(status.titleKey)}</StatusBadge>
  }

  return (
    <>
      <HistoryHeader>
        <HistoryHeaderTopRow>
          <HeaderTitleGroup>
            <ClockIconButton><History /></ClockIconButton>
            <HistoryTitle>{t`status_log_title`}</HistoryTitle>
          </HeaderTitleGroup>
          <BackButton onClick={onBack}>{t`close`}</BackButton>
        </HistoryHeaderTopRow>
        <Breadcrumb>
          {t`breadcrumb_root`} &nbsp;›&nbsp; {t`breadcrumb_log`} &nbsp;›&nbsp; <BreadcrumbActive>{t`protocol_badge`} #{protocolId}</BreadcrumbActive>
        </Breadcrumb>
        <FilterCardRow>
          <ProtocolBadgeCard>
            <Image src={DocIcon} />
            <ProtocolBadgeTexts>
              <ProtocolBadgeLabel>{t`protocol_badge`}</ProtocolBadgeLabel>
              <ProtocolBadgeTitle>{protocolTitle || `${t`protocol_badge`} #${protocolId}`}</ProtocolBadgeTitle>
            </ProtocolBadgeTexts>
          </ProtocolBadgeCard>

          <DateFieldsRow>
            <DateField>
              <StyledLabel>{t`from_date`}</StyledLabel>
              <DateInputWithCalendar
                value={fromDate}
                onChange={(date: Date | null) => { setPage(0); setFromDate(date) }}
                locale={user.dateFnsLocal}
                placeholder="dd/mm/yyyy"
              />
            </DateField>
            <DateField>
              <StyledLabel>{t`to_date`}</StyledLabel>
              <DateInputWithCalendar
                value={toDate}
                onChange={(date: Date | null) => { setPage(0); setToDate(date) }}
                locale={user.dateFnsLocal}
                placeholder="dd/mm/yyyy"
              />
            </DateField>
          </DateFieldsRow>
        </FilterCardRow>
      </HistoryHeader>

      <HistoryBody>
        <TableContainerStyled>
          <Table stickyHeader>
            <TableHeadStyled>
              <TableRow>
                <HeadCell>{t`col_datetime`}</HeadCell>
                <HeadCell>{t`col_changed_by`}</HeadCell>
                <HeadCellCentered>{t`col_status_from`}</HeadCellCentered>
                <HeadCellCentered></HeadCellCentered>
                <HeadCellCentered>{t`col_status_to`}</HeadCellCentered>
                <HeadCell>{t`col_notes`}</HeadCell>
              </TableRow>
            </TableHeadStyled>
            <TableBody>
              {loadingProtocolStatusHistory && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Spinner />
                  </TableCell>
                </TableRow>
              )}
              {!loadingProtocolStatusHistory && protocolStatusHistory.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <ProtocolsEmpty description={t`status_log_empty`} />
                  </TableCell>
                </TableRow>
              )}
              {!loadingProtocolStatusHistory && protocolStatusHistory.map(item => (
                <TableRow key={item.id} hover>
                  <TableCellStyled>
                    <DateCell>{new Date(item.createdDate).toLocaleString('he-IL')}</DateCell>
                  </TableCellStyled>
                  <TableCellStyled>
                    <EditorCell><Image src={User4Icon} /> {item.editorName}</EditorCell>
                  </TableCellStyled>
                  <TableCellCentered>{renderStatus(item.originalStatusId)}</TableCellCentered>
                  {/* The table is RTL: the original status sits to the right of the new one,
                      so the arrow points left - from the original status to the new one. */}
                  <TableCellCentered><StatusTransitionCell><ArrowBack style={{ fontSize: 16, color: '#94A3B8' }} /></StatusTransitionCell></TableCellCentered>
                  <TableCellCentered>{renderStatus(item.newStatusId)}</TableCellCentered>
                  {/* Only a rejection carries a note - the reason the approver gave. */}
                  <TableCellStyled>
                    {item.newStatusId === 4 && <NoteCell>{item.rejectReason || '—'}</NoteCell>}
                  </TableCellStyled>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainerStyled>
        <MekomeTablePagination
          component="div"
          count={protocolStatusHistoryTotalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </HistoryBody>
    </>
  )
}

export default observer(ProtocolStatusHistoryView)
